import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errMsg, API_BASE } from '../lib/api';
import { getToken } from '../lib/auth';
import MediaUploader from '../components/MediaUploader';
import ArticleManager from '../components/ArticleManager';
import TemplateThumb from '../components/TemplateThumb';
import NotaryBadge from '../components/NotaryBadge';
import { Icon } from '../lib/icons';

interface MemorialData {
  id: string;
  slug: string;
  ownerId: string;
  deceasedName: string;
  deceasedNameEn: string | null;
  birthDate: string | null;
  deathDate: string | null;
  biography: string | null;
  epitaph: string | null;
  visibility: string;
  themeId: string;
  templateId: string;
  tributeCount: number;
  /** 馆内逝者；双人合葬会有 2 位（sortOrder 0 为主逝者） */
  people?: {
    id: string;
    name: string;
    gender: string | null;
    birthDate: string | null;
    deathDate: string | null;
    portraitUrl: string | null;
    sortOrder: number;
  }[];
}

/** 第二位逝者的编辑表单（主逝者直接沿用上方姓名/生卒字段） */
interface SecondPersonForm {
  name: string;
  gender: string;
  birthDate: string;
  deathDate: string;
  /**
   * 遗像 R2 key，三态：
   * undefined = 未改动（后端保留原 key）
   * null      = 明确移除
   * 字符串     = 新上传
   */
  portraitKey: string | null | undefined;
  /** 预览用：签名地址或本地 objectURL */
  portraitUrl: string | null;
}

interface TemplateItem {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  bgSvg?: string | null;
  layout?: string | null;
}
interface ThemeItem {
  id: string;
  slug: string;
  name: string;
  config: Record<string, string>;
}

export default function MemorialEdit() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [memorial, setMemorial] = useState<MemorialData | null>(null);
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [themes, setThemes] = useState<ThemeItem[]>([]);
  const [form, setForm] = useState({
    deceasedName: '',
    deceasedNameEn: '',
    birthDate: '',
    deathDate: '',
    biography: '',
    epitaph: '',
    visibility: 'public',
    templateId: 'classic',
    themeId: 'ink',
  });
  /** 合葬的第二位逝者为 null 表示单人馆 */
  const [second, setSecond] = useState<SecondPersonForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    Promise.all([
      api<{ memorial: MemorialData }>(`/memorials/${slug}`),
      api<{ items: TemplateItem[] }>('/templates'),
      api<{ items: ThemeItem[] }>('/themes'),
    ]).then(([m, t, th]) => {
      if (m.ok && m.data?.memorial) {
        const d = m.data.memorial;
        setMemorial(d);
        setForm({
          deceasedName: d.deceasedName ?? '',
          deceasedNameEn: d.deceasedNameEn ?? '',
          birthDate: d.birthDate ?? '',
          deathDate: d.deathDate ?? '',
          biography: d.biography ?? '',
          epitaph: d.epitaph ?? '',
          visibility: d.visibility ?? 'public',
          templateId: d.templateId ?? 'classic',
          themeId: d.themeId ?? 'ink',
        });
        // 已有合葬对象时回填表单（sortOrder > 0 即第二位）
        const partner = (d.people ?? []).find((p) => p.sortOrder > 0);
        setSecond(
          partner
            ? {
                name: partner.name,
                gender: partner.gender ?? '',
                birthDate: partner.birthDate ?? '',
                deathDate: partner.deathDate ?? '',
                portraitKey: undefined, // 未改动，后端保留原 key
                portraitUrl: partner.portraitUrl ?? null,
              }
            : null
        );
      } else {
        setError(errMsg(m, '加载失败'));
      }
      if (t.ok && t.data) setTemplates(t.data.items);
      if (th.ok && th.data) setThemes(th.data.items);
      setLoading(false);
    });
  }, [slug]);

  function set<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
    setSaved(false);
  }

  function setSecondField<K extends keyof SecondPersonForm>(k: K, v: string) {
    setSecond((s) => (s ? { ...s, [k]: v } : s));
    setSaved(false);
  }

  /**
   * 第二位逝者遗像：上传到 R2 并返回 object key。
   * 与主逝者头像走同一套 portrait-presign 流程（不计入相册额度），
   * key 由后端带 UUID 生成，不会覆盖主逝者头像。
   */
  async function uploadSecondPortrait(file: File): Promise<string | null> {
    const pres = await api<{
      mode: 'presigned' | 'direct';
      key: string;
      uploadUrl?: string;
    }>('/media/portrait-presign', {
      method: 'POST',
      body: JSON.stringify({
        memorialId: memorial!.id,
        filename: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      }),
    });
    if (!pres.ok || !pres.data) return null;

    const { mode, key, uploadUrl } = pres.data;
    try {
      if (mode === 'presigned' && uploadUrl) {
        const up = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!up.ok) return null;
      } else {
        const token = getToken();
        const up = await fetch(`${API_BASE}/media/upload?key=${encodeURIComponent(key)}`, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!up.ok) return null;
      }
      return key;
    } catch {
      return null;
    }
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);

    // 主逝者沿用上方表单字段，第二位（合葬）拼在后面一并提交
    const res = await api<{ memorial: MemorialData }>(`/memorials/${memorial!.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        ...form,
        people: [
          {
            name: form.deceasedName,
            birthDate: form.birthDate,
            deathDate: form.deathDate,
          },
          ...(second && second.name.trim()
            ? [
                {
                  name: second.name.trim(),
                  gender: second.gender || null,
                  birthDate: second.birthDate || null,
                  deathDate: second.deathDate || null,
                  // undefined 时不序列化，后端据此保留原有遗像；null 表示移除
                  portraitKey: second.portraitKey,
                },
              ]
            : []),
        ],
      }),
    });

    setSaving(false);

    if (res.ok) {
      setSaved(true);
    } else if (res.error?.code === 'FORBIDDEN') {
      setDenied(true);
    } else {
      setError(errMsg(res, '保存失败'));
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50">
        <p className="text-sm text-ink-500">加载中…</p>
      </div>
    );
  }

  if (!memorial) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50 px-6">
        <div className="text-center">
          <p className="text-sm text-ink-600">{error || '纪念馆不存在'}</p>
          <Link to="/" className="mt-4 inline-block text-xs text-ink-500 underline">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-ink-100 to-ink-50 px-6 py-8">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="font-serifcn text-xl text-ink-900">编辑纪念馆</h1>
          <Link
            to={`/m/${memorial.slug}`}
            className="rounded-full border border-ink-300 px-4 py-1.5 text-xs text-ink-700"
          >
            查看前台 →
          </Link>
        </div>

        {denied && (
          <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            你没有编辑权限。只有创建者或有编辑权限的家属可以修改。
          </div>
        )}

        <form onSubmit={save} className="rounded-2xl border border-ink-200 bg-white/80 p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="逝者姓名" required>
              <input
                value={form.deceasedName}
                onChange={(e) => set('deceasedName', e.target.value)}
                className={INPUT}
                required
              />
            </Field>
            <Field label="英文名">
              <input
                value={form.deceasedNameEn}
                onChange={(e) => set('deceasedNameEn', e.target.value)}
                className={INPUT}
              />
            </Field>
            <Field label="出生日期">
              <input
                type="date"
                value={form.birthDate}
                onChange={(e) => set('birthDate', e.target.value)}
                className={INPUT}
              />
            </Field>
            <Field label="忌日">
              <input
                type="date"
                value={form.deathDate}
                onChange={(e) => set('deathDate', e.target.value)}
                className={INPUT}
              />
            </Field>
          </div>

          <Field label="墓志铭">
            <input
              value={form.epitaph}
              onChange={(e) => set('epitaph', e.target.value)}
              className={INPUT}
            />
          </Field>

          <Field label="生平简介">
            <textarea
              value={form.biography}
              onChange={(e) => set('biography', e.target.value)}
              rows={4}
              className="w-full rounded-xl border border-ink-200 bg-ink-50/50 p-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
            />
          </Field>

          {/* 合葬：可添加第二位逝者 */}
          <div className="mb-6 rounded-xl border border-ink-200 bg-ink-50/40 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-medium text-ink-700">合葬（第二位逝者）</p>
                <p className="mt-0.5 text-[11px] leading-relaxed text-ink-500">
                  夫妻合葬等场景可添加，前台会并排显示两位逝者
                </p>
              </div>
              {second ? (
                <button
                  type="button"
                  onClick={() => setSecond(null)}
                  className="shrink-0 rounded-full border border-ink-300 px-3 py-1 text-[11px] text-ink-700 transition hover:bg-ink-100"
                >
                  移除
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    setSecond({
                      name: '',
                      gender: '',
                      birthDate: '',
                      deathDate: '',
                      portraitKey: undefined,
                      portraitUrl: null,
                    })
                  }
                  className="shrink-0 rounded-full bg-ink-900 px-3 py-1 text-[11px] text-ink-50 transition hover:bg-ink-800"
                >
                  + 添加
                </button>
              )}
            </div>

            {second && (
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Field label="姓名" required>
                  <input
                    value={second.name}
                    onChange={(e) => setSecondField('name', e.target.value)}
                    className={INPUT}
                    placeholder="第二位逝者姓名"
                  />
                </Field>
                <Field label="性别">
                  <select
                    value={second.gender}
                    onChange={(e) => setSecondField('gender', e.target.value)}
                    className={INPUT}
                  >
                    <option value="">未填写</option>
                    <option value="male">男</option>
                    <option value="female">女</option>
                  </select>
                </Field>
                <Field label="出生日期">
                  <input
                    type="date"
                    value={second.birthDate}
                    onChange={(e) => setSecondField('birthDate', e.target.value)}
                    className={INPUT}
                  />
                </Field>
                <Field label="忌日">
                  <input
                    type="date"
                    value={second.deathDate}
                    onChange={(e) => setSecondField('deathDate', e.target.value)}
                    className={INPUT}
                  />
                </Field>

                {/* 第二位逝者遗像 */}
                <div className="sm:col-span-2 flex items-center gap-4">
                  <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-full border-2 border-ink-200 bg-ink-100">
                    {second.portraitUrl ? (
                      <img
                        src={second.portraitUrl}
                        alt="第二位逝者遗像"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-2xl text-ink-400">
                        <Icon name="person" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="mb-1.5 text-xs text-ink-600">遗像（可选）</p>
                    <label className="cursor-pointer rounded-full bg-ink-900 px-4 py-2 text-xs text-ink-50">
                      {second.portraitUrl ? '更换照片' : '上传照片'}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          const key = await uploadSecondPortrait(f);
                          if (key) {
                            setSecond((s) =>
                              s
                                ? {
                                    ...s,
                                    portraitKey: key,
                                    portraitUrl: URL.createObjectURL(f),
                                  }
                                : s
                            );
                            setSaved(false);
                          } else {
                            setError('遗像上传失败，请重试');
                          }
                          e.currentTarget.value = '';
                        }}
                      />
                    </label>
                    {second.portraitUrl && (
                      <button
                        type="button"
                        onClick={() =>
                          setSecond((s) =>
                            s ? { ...s, portraitKey: null, portraitUrl: null } : s
                          )
                        }
                        className="mt-1.5 block text-[11px] text-ink-500 underline"
                      >
                        移除遗像
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 外观切换 */}
          <div className="mb-6 rounded-xl border border-ink-200 bg-ink-50/40 p-4">
            <p className="mb-3 text-xs font-medium text-ink-700">页面风格</p>
            <div className="mb-4 grid gap-2 sm:grid-cols-2">
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => set('templateId', t.slug)}
                  className={`rounded-xl border p-3 text-left transition ${
                    form.templateId === t.slug
                      ? 'border-ink-900 bg-white'
                      : 'border-ink-200 bg-white/60 hover:border-ink-400'
                  }`}
                >
                  <TemplateThumb slug={t.slug} layout={t.layout} bgSvg={t.bgSvg} className="mb-2 text-ink-800" />
                  <p className="text-sm text-ink-900">{t.name}</p>
                  {t.description && (
                    <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-ink-500">
                      {t.description}
                    </p>
                  )}
                </button>
              ))}
            </div>

            <p className="mb-2 text-xs font-medium text-ink-700">配色</p>
            <div className="flex flex-wrap gap-2">
              {themes.map((th) => (
                <button
                  key={th.id}
                  type="button"
                  onClick={() => set('themeId', th.slug)}
                  className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition ${
                    form.themeId === th.slug
                      ? 'border-ink-900 bg-white'
                      : 'border-ink-200 bg-white/60'
                  }`}
                >
                  <span className="flex gap-1">
                    <span className="h-3 w-3 rounded-full" style={{ background: th.config['--c-bg'] }} />
                    <span className="h-3 w-3 rounded-full" style={{ background: th.config['--c-accent'] }} />
                    <span className="h-3 w-3 rounded-full" style={{ background: th.config['--c-text'] }} />
                  </span>
                  {th.name}
                </button>
              ))}
            </div>
          </div>

          <Field label="隐私设置">
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: 'public', t: '公开' },
                { v: 'password', t: '密码访问' },
                { v: 'private', t: '仅家属' },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => set('visibility', o.v)}
                  className={`h-10 rounded-xl border text-xs transition ${
                    form.visibility === o.v
                      ? 'border-ink-900 bg-ink-900 text-ink-50'
                      : 'border-ink-200 text-ink-600'
                  }`}
                >
                  {o.t}
                </button>
              ))}
            </div>
          </Field>

          {error && (
            <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
          )}
          {saved && (
            <p className="mb-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              已保存
            </p>
          )}

          <button
            type="submit"
            disabled={saving}
            className="h-11 w-full rounded-xl bg-ink-900 text-sm text-ink-50 transition hover:bg-ink-800 disabled:opacity-50"
          >
            {saving ? '保存中…' : '保存修改'}
          </button>
        </form>

        {/* 相册 */}
        <div className="mt-6 rounded-2xl border border-ink-200 bg-white/80 p-6">
          <MediaUploader memorialId={memorial.id} />
        </div>

        {/* 追思文章（馆主撰写/管理） */}
        <div className="mt-6 rounded-2xl border border-ink-200 bg-white/80 p-6">
          <ArticleManager memorialId={memorial.id} />
        </div>

        {/* 链上存证（馆主可购买/查看；后台未配置时组件自动不渲染） */}
        <div className="mt-6">
          <NotaryBadge memorialId={memorial.id} productId="pack_notary" canBuy />
        </div>


      </div>
    </div>
  );
}

const INPUT =
  'h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white';

function Field({
  label,
  children,
  required,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <div className="mb-4">
      <label className="mb-1.5 block text-xs text-ink-600">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}
